import React, { useContext, useState, useEffect} from 'react';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import { FormContext } from '../../../../contexts/generated-content/content-form-context';
import SelectSmall from '../../../general-components/select-small';
import AnimationRangeInput from './animation-range-input';
import DurationInput from '../../../general-components/duration-input';
import { animationTiming, animateInOptions} from './types';
import AnimationSectionHeader from './animation-section-header';
import { AnimationsContext } from './animations-context';
import { DURATION_INPUT_TYPES } from '../../../../types/constants';

const maxValue = 15;
const minValue = 3;

export default function DurationSectionAnimations({isStylepack = false}) {
	const formContext = useContext(FormContext);
	const { statefulFormValues, onChange } = formContext || {};

	const {
		handleAnimationInChange,
		handleShowForDurationChange,
		selectedInTransition,
		delayInDuration,
		setDelayInDuration,
		showFor
	} = useContext(AnimationsContext);
	
	const [localDuration, setLocalDuration] = useState(maxValue);
	const [dropdownOption, setDropdownOption] = useState(selectedInTransition);
	
	useEffect(() => {
		const formVal = Number(statefulFormValues.seconds);
		if (!isNaN(formVal)) {
			// stylepack item, use form value
			setLocalDuration(Math.max(formVal, 3))
		} else if (showFor > 0) {
			// else use basic overlay animations
			setLocalDuration(Math.max(showFor, 3))
		} else {
			// never been set
			if (isStylepack) {
				//stylepack items default to animate out after 8s
				setLocalDuration(8.5);
			} else {
				// by default, items show for forever
				setLocalDuration(maxValue);
			}
		}
	}, [statefulFormValues?.seconds, showFor, isStylepack]);

	useEffect(() => {
		const initialValue = animateInOptions.find((op) => op.value === selectedInTransition.value);
		if (initialValue) setDropdownOption(initialValue);
	}, [selectedInTransition])
	
	// Debounced callback to update form after 500ms of no changes
	const formUpdate = (value: number) => {
		const isInf = value >= maxValue;
		//@ts-expect-error as ChangeEvent<HTMLInputElement>
		onChange({target: {name: 'seconds', value: isInf ? 8 : value}});
		//@ts-expect-error as ChangeEvent<HTMLInputElement>
		onChange({target: {name: 'animate-out', value: !isInf}});
	};
	
	const handleSliderChange = (value: number) => {
		setLocalDuration(value); // Immediate UI update
		
		// Use callback if provided, otherwise use form context
		if (isStylepack) {
			formUpdate(value); // Debounced form update
		} else {
			handleShowForDurationChange(value >= maxValue ? -1 : value);
		}
	};

	const handleAnimationStartChoice = (choice: animationTiming) => {
		const option = animateInOptions.find((op) => op.value === choice) ?? animateInOptions[0];
		setDropdownOption(option);
		handleAnimationInChange(option.value);
	}

	return (<>
			<div style={{marginTop: '16px'}}/>
			<div className='flex-center'>

				<AnimationSectionHeader
					text='Show for'
					icon={ICONS.ANIMATION_TIMER}
				/>
				<DurationInput
					inputType={DURATION_INPUT_TYPES.SECONDS}
					className='stylepack-animation-input-container'
					onBlur={(value: number) => handleSliderChange(value)}
					value={localDuration}
					minValue={`${minValue}`}
					maxValue={`${maxValue}`}
					iconOnMax='∞'
					onChangeDebounce
				/>
			</div>
			<AnimationRangeInput
				value={localDuration}
				onChange={handleSliderChange}
				min={minValue}
				max={maxValue}
				step={0.5}
				ticks={[
					{ label: '3s', position: 'left' },
					{ label: '6s', position: 'left' },
					{ label: '9s', position: 'center' },
					{ label: '12s', position: 'right' },
					{ label: '∞', position: 'right', isInfinity: true },
				]}
			/>
			<div className='flex-center justify-space-between margin-top-medium'>
				<SelectSmall
					options={animateInOptions}
					label={dropdownOption.label}
					selected={dropdownOption}
					onChange={(value) => handleAnimationStartChoice(value)}
					hasCarrot={true}
					className='animation-select-dropdown'
				/>
				{dropdownOption?.value === animationTiming.delayed ? (
					<DurationInput
						inputType={DURATION_INPUT_TYPES.SECONDS}
						value={delayInDuration}
						onBlur={(value: number) => setDelayInDuration(value)}
						minValue={'0'}
					/>
				) : null}
			</div>
			
	</>);
}